import React from 'react';
import { classNames } from '../lib/utils';
import { tiers } from '../lib/data';
import { CheckIcon } from './Icons';
import { useDialog } from '../contexts/DialogContext';

export const Pricing: React.FC = () => {
  const { openDialog } = useDialog();

  return (
    <div id="pricing" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base/7 font-semibold" style={{ color: '#00D084' }}>Pricing</h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl lg:text-balance" style={{ color: '#241E61' }}>
            Pay for production, not for seats
          </p>
          <p className="mt-6 text-lg/8 text-gray-600">
            Every plan includes unlimited scans. You pay for the number of product tags you create — scaling with your manufacturing volume. QR codes never expire.
          </p>
        </div>
        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier, tierIdx) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular ? 'lg:z-10 lg:rounded-b-none' : 'lg:mt-8',
                tierIdx === 0 ? 'lg:rounded-r-none' : '',
                tierIdx === tiers.length - 1 ? 'lg:rounded-l-none' : '',
                'flex flex-col justify-between rounded-3xl bg-white p-8 ring-1 ring-gray-200 xl:p-10',
              )}
            >
              <div>
                <div className="flex items-center justify-between gap-x-4">
                  <h3
                    id={tier.id}
                    className={classNames(
                      tier.mostPopular ? 'text-purple-600' : 'text-gray-900',
                      'text-lg/8 font-semibold',
                    )}
                  >
                    {tier.name}
                  </h3>
                  {tier.mostPopular ? (
                    <p className="rounded-full bg-purple-600/10 px-2.5 py-1 text-xs/5 font-semibold text-purple-600">
                      Most popular
                    </p>
                  ) : null}
                </div>
                <p className="mt-4 text-sm/6 text-gray-600">{tier.description}</p>
                <p className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-4xl font-semibold tracking-tight text-gray-900">
                    {(tier as any).priceMonthly || 'Custom'}
                  </span>
                  {(tier as any).priceMonthly && (
                    <span className="text-sm/6 font-semibold text-gray-600">/month</span>
                  )}
                </p>
                {(tier as any).priceAnnual && (
                  <p className="mt-1 text-xs text-gray-500">
                    {(tier as any).priceAnnual}/mo billed annually (save 20%)
                  </p>
                )}
                {(tier as any).tagline && (
                  <p className="mt-2 text-sm font-medium" style={{ color: '#00D084' }}>
                    {(tier as any).tagline}
                  </p>
                )}
                {(tier as any).overage && (
                  <p className="text-xs text-gray-500">
                    {(tier as any).overage}
                  </p>
                )}
                <ul role="list" className="mt-8 space-y-3 text-sm/6 text-gray-600">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      {feature.endsWith(':') ? (
                        <span className="font-semibold text-gray-900">{feature}</span>
                      ) : (
                        <>
                          <CheckIcon className="h-6 w-5 flex-none text-purple-600" />
                          {feature}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => {
                  if (tier.name === 'Enterprise') {
                    window.location.href = 'mailto:hello@brandedmark.com?subject=Enterprise%20Inquiry';
                  } else {
                    openDialog('waitlist');
                  }
                }}
                aria-describedby={tier.id}
                className={classNames(
                  tier.mostPopular
                    ? 'text-white shadow-sm hover:opacity-90'
                    : 'text-purple-600 ring-1 ring-inset ring-purple-200 hover:ring-purple-300',
                  'mt-8 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-colors cursor-pointer',
                )}
                style={tier.mostPopular ? { backgroundColor: '#241E61' } : undefined}
              >
                {tier.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
